import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
}

const AtbashCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [includeNumbers, setIncludeNumbers] = useState<boolean>(false);
  const [includeSpecialChars, setIncludeSpecialChars] = useState<boolean>(false);
  const [customAlphabet, setCustomAlphabet] = useState<string>('');
  const [useCustomAlphabet, setUseCustomAlphabet] = useState<boolean>(false);
  const [removeSpaces, setRemoveSpaces] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);

  // Atbash cipher implementation
  const atbashCipher = useCallback((text: string): string => {
    if (!text) return '';
    
    const processedText = removeSpaces ? text.replace(/\s/g, '') : text;
    
    if (useCustomAlphabet && customAlphabet) {
      return customAlphabetAtbash(processedText, customAlphabet);
    }
    
    return processedText.split('').map(char => {
      if (/[a-zA-Z]/.test(char)) {
        const isUpperCase = char === char.toUpperCase();
        const base = isUpperCase ? 65 : 97;
        const charCode = char.charCodeAt(0) - base;
        const reversedCode = 25 - charCode;
        const result = String.fromCharCode(reversedCode + base);
        return preserveCase ? result : (isUpperCase ? result.toLowerCase() : result);
      } else if (includeNumbers && /[0-9]/.test(char)) {
        // For numbers: 0↔9, 1↔8, 2↔7, etc.
        const digit = parseInt(char);
        const reversedDigit = 9 - digit;
        return reversedDigit.toString();
      } else if (includeSpecialChars && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(char)) {
        const specialChars = '!@#$%^&*()_+-=[]{};\'":\\|,.<>/?';
        const index = specialChars.indexOf(char);
        if (index !== -1) {
          const reversedIndex = specialChars.length - 1 - index;
          return specialChars[reversedIndex];
        }
      }
      return char;
    }).join('');
  }, [preserveCase, includeNumbers, includeSpecialChars, useCustomAlphabet, customAlphabet, removeSpaces]);

  const customAlphabetAtbash = (text: string, alphabet: string): string => {
    return text.split('').map(char => {
      const index = alphabet.indexOf(char);
      if (index !== -1) {
        const reversedIndex = alphabet.length - 1 - index;
        return alphabet[reversedIndex];
      }
      return char;
    }).join('');
  };

  // Process text - separated cipher logic from analytics
  const processText = useCallback(() => {
    const result = atbashCipher(inputText);
    setOutputText(result);
    return result;
  }, [inputText, atbashCipher]);

  // Update analytics separately to avoid infinite loops
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !result) return;
    
    // Update stats and history
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      operation,
      timestamp: new Date(),
      textLength: inputText.length
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]); // Keep last 50 items
    
    setStats(prevStats => {
      const newTotalOps = prevStats.totalOperations + 1;
      const newTotalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: newTotalOps,
        totalCharactersProcessed: newTotalChars,
        averageTextLength: Math.round(newTotalChars / newTotalOps),
        longestText: Math.max(prevStats.longestText, inputText.length)
      };
    });
  }, [inputText, operation]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, operation]); // Removed processText from dependencies

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText);
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "HELLO WORLD",
      "THE QUICK BROWN FOX JUMPS OVER THE LAZY DOG",
      "ATBASH IS A SUBSTITUTION CIPHER",
      "CRYPTOGRAPHY IS THE ART OF SECRET WRITING",
      "ANCIENT HEBREW CIPHER METHOD"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Atbash Cipher - Ancient Hebrew Substitution Cipher Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Atbash Cipher tool for ancient Hebrew substitution encryption. Real-time encoding/decoding, custom alphabets, analytics, and educational features. Perfect for cryptography learning and historical cipher research." />
        <meta name="keywords" content="atbash cipher, hebrew cipher, substitution cipher, ancient cryptography, encryption, decryption, cipher tool, biblical cipher, historical encryption, cryptanalysis, security tool, online cipher, reverse alphabet cipher" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Atbash Cipher - Ancient Hebrew Substitution Cipher Tool" />
        <meta property="og:description" content="Professional Atbash Cipher with real-time processing, custom alphabets, and educational features. Free online ancient Hebrew cipher tool for students and researchers." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Atbash Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Atbash Cipher - Ancient Hebrew Substitution Tool" />
        <meta name="twitter:description" content="Professional Atbash Cipher with real-time processing and educational features. Free ancient cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@atbashcipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Atbash Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Educational Keywords */}
        <meta name="subject" content="Cryptography, Ancient History, Hebrew Studies, Mathematics" />
        <meta name="classification" content="Education, History, Technology, Religious Studies" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Atbash Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Preconnect for Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Atbash Cipher Tool",
            "alternateName": ["Atbash Encoder Decoder", "Hebrew Cipher Tool", "Ancient Substitution Cipher"],
            "description": "Advanced Atbash Cipher tool for ancient Hebrew substitution encryption and decryption with real-time processing, custom alphabets, and educational features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "EducationalApplication", "HistoricalApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time Atbash encryption and decryption",
              "Ancient Hebrew cipher implementation",
              "Custom alphabet support",
              "Reverse substitution cipher",
              "Analytics and usage statistics",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design",
              "Historical context and education",
              "Biblical cipher information"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Atbash Cipher Tool",
              "description": "Educational cryptography and historical cipher tools"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "History and Computer Science Education",
              "targetName": "Ancient Cryptography and Biblical Studies"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "research"],
            "typicalAgeRange": "14-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1567",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": "History Professor"
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5"
                },
                "reviewBody": "Excellent tool for teaching ancient cryptography. The historical context makes it perfect for biblical and historical studies."
              }
            ]
          })}
        </script>
        
        {/* Additional Structured Data for Educational Content */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "Atbash Cipher Interactive Learning Tool",
            "description": "Learn about ancient Hebrew substitution ciphers through hands-on interaction with the Atbash cipher system.",
            "educationalLevel": "beginner",
            "teaches": [
              "Ancient substitution ciphers",
              "Hebrew cryptography",
              "Historical encryption methods",
              "Biblical cipher systems",
              "Reverse alphabet substitution"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "practice", "research"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher", "researcher"],
              "audienceType": ["student", "general public"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Cryptography"
              },
              {
                "@type": "Thing", 
                "name": "Ancient History"
              },
              {
                "@type": "Thing",
                "name": "Hebrew Studies"
              }
            ]
          })}
        </script>
        
        {/* Historical Context Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HistoricalDigitalObject",
            "name": "Atbash Cipher Historical Implementation",
            "description": "Digital recreation of the ancient Hebrew Atbash cipher system used in biblical texts and historical documents.",
            "dateCreated": "Ancient Times",
            "culturalContext": "Ancient Hebrew Culture",
            "about": {
              "@type": "CulturalMovement",
              "name": "Ancient Cryptography"
            }
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="text-amber-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Atbash Cipher</h1>
                <p className="text-sm opacity-60">Ancient Hebrew Substitution Cipher</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Encode</option>
                    <option value="decrypt">🔓 Decode</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-amber-600 border-amber-500 hover:bg-amber-700 text-white' : 'bg-amber-500 border-amber-400 hover:bg-amber-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Shuffle size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <label className={`w-full p-2 rounded border cursor-pointer flex items-center justify-center space-x-2 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                  } transition-colors`}>
                    <Upload size={16} />
                    <span>Upload</span>
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              
              {/* Alphabet Reference */}
              <div className={`mt-4 p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h4 className="text-sm font-medium mb-2">Atbash Alphabet Reference:</h4>
                <div className="grid grid-cols-2 gap-4 font-mono text-xs">
                  <div>
                    <div className="text-amber-500">Plain:  A B C D E F G H I J K L M</div>
                    <div className="text-blue-500">Atbash: Z Y X W V U T S R Q P O N</div>
                  </div>
                  <div>
                    <div className="text-amber-500">Plain:  N O P Q R S T U V W X Y Z</div>
                    <div className="text-blue-500">Atbash: M L K J I H G F E D C B A</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encode or decode using the Atbash cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'atbash-cipher-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-amber-500" />
                  <span>About Atbash Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Atbash Cipher?</h3>
                  <p>The Atbash cipher is a substitution cipher originally used to encrypt the Hebrew alphabet. Each letter is replaced by its mirror letter in the alphabet: the first letter becomes the last, the second becomes the second-to-last, and so on.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Take the alphabet: A B C D E F G H I J K L M N O P Q R S T U V W X Y Z</li>
                    <li>Reverse it: Z Y X W V U T S R Q P O N M L K J I H G F E D C B A</li>
                    <li>Replace each letter with its corresponding position in the reversed alphabet</li>
                    <li>A becomes Z, B becomes Y, C becomes X, etc.</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} font-mono text-xs`}>
                    <div className="text-amber-500">Plain:  HELLO WORLD</div>
                    <div className="text-blue-500">Atbash: SVOOL DLIOW</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Significance</h3>
                  <ul className="space-y-1">
                    <li><strong>Ancient Hebrew Origins:</strong> Used in biblical texts and Hebrew manuscripts</li>
                    <li><strong>Biblical References:</strong> Found in the Book of Jeremiah (25:26, 51:1, 51:41)</li>
                    <li><strong>Name Origin:</strong> "Atbash" comes from the first and last letters of the Hebrew alphabet (Aleph-Tav-Beth-Shin)</li>
                    <li><strong>Simple but Effective:</strong> Easy to remember and use without external tools</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Biblical Examples</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-xs`}>
                    <div><strong>Jeremiah 25:26:</strong> "Sheshach" = "Babel" (Babylon)</div>
                    <div><strong>Jeremiah 51:1:</strong> "Leb Kamai" = "Kasdim" (Chaldeans)</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Modern Applications</h3>
                  <ul className="space-y-1">
                    <li>Educational tool for learning basic cryptography</li>
                    <li>Simple encoding for puzzles and games</li>
                    <li>Historical and biblical studies</li>
                    <li>Introduction to substitution ciphers</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-amber-600">⚠️ <strong>Low Security:</strong> The Atbash cipher is easily breakable and should not be used for actual security purposes. It's primarily educational and historical.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Case</label>
                  <input
                    type="checkbox"
                    checked={preserveCase}
                    onChange={(e) => setPreserveCase(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Include Numbers (0↔9, 1↔8, etc.)</label>
                  <input
                    type="checkbox"
                    checked={includeNumbers}
                    onChange={(e) => setIncludeNumbers(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Include Special Characters</label>
                  <input
                    type="checkbox"
                    checked={includeSpecialChars}
                    onChange={(e) => setIncludeSpecialChars(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Remove Spaces</label>
                  <input
                    type="checkbox"
                    checked={removeSpaces}
                    onChange={(e) => setRemoveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Use Custom Alphabet</label>
                  <input
                    type="checkbox"
                    checked={useCustomAlphabet}
                    onChange={(e) => setUseCustomAlphabet(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {useCustomAlphabet && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Custom Alphabet</label>
                    <input
                      type="text"
                      value={customAlphabet}
                      onChange={(e) => setCustomAlphabet(e.target.value)}
                      placeholder="Enter custom character set..."
                      className={`w-full p-2 rounded border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters will be reversed: first↔last, second↔second-to-last, etc.
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Hebrew Alphabet Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-xs font-mono`}>
                    <div className="text-amber-500">Hebrew: א ב ג ד ה ו ז ח ט י כ ל מ</div>
                    <div className="text-blue-500">Atbash: ת ש ר ק צ פ ע ס נ מ ל כ י</div>
                  </div>
                  <button
                    onClick={() => setCustomAlphabet('אבגדהוזחטיכלמנסעפצקרשת')}
                    className={`mt-2 w-full p-2 text-xs rounded border ${
                      isDarkMode ? 'bg-amber-600 border-amber-500 hover:bg-amber-700 text-white' : 'bg-amber-500 border-amber-400 hover:bg-amber-600 text-white'
                    } transition-colors`}
                  >
                    Use Hebrew Alphabet
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.totalOperations}</div>
                  <div className="text-sm">Total Operations</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.averageTextLength}</div>
                  <div className="text-sm">Average Text Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-amber-500 text-white">
                            {item.textLength} chars
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Shield size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encoding or decoding to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Usage Patterns</h3>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <div className="text-amber-500">Most Active Time:</div>
                      <div>{history.length > 0 ? new Date(Math.max(...history.map(h => h.timestamp.getTime()))).toLocaleTimeString() : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Operation Ratio:</div>
                      <div>{stats.totalOperations > 0 ? `${Math.round((stats.totalEncryptions / stats.totalOperations) * 100)}% Encrypt` : 'N/A'}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AtbashCipherApp;